import React, { Suspense, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'

// Componente App inline para evitar problemas de importação
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, HashRouter } from "react-router-dom";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { AuthProvider } from "@/hooks/useAuth";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import RouterWrapper from "@/components/RouterWrapper";
import { RealtimeSyncManager } from "@/components/RealtimeSyncManager";
import CookieConsentBanner from "@/components/lgpd/CookieConsentBanner";
import ScrollToTop from "@/components/ScrollToTop";
import { OfflineIndicator } from "@/components/OfflineIndicator";
const PWALifeCycleLazy = React.lazy(() => import('@/components/PWALifeCycle'))

// Centralize QueryClient singleton
import { queryClient } from "@/lib/react-query";

import { initializeDatabase } from '@/lib/db/init';
import { isTauri } from '@/lib/db/db';
import Preloader from '@/components/Preloader';
import { LicenseActivation } from '@/components/auth/LicenseActivation';

const Router = isTauri() ? HashRouter : BrowserRouter;

const App = () => {
  const [mounted, setMounted] = useState(false)
  const [dbReady, setDbReady] = useState(false)
  const [licenseValid, setLicenseValid] = useState(false)

  useEffect(() => {
    setMounted(true)
    initializeDatabase().then(async () => {
      const { checkLocalLicenseValidity } = await import('@/lib/db/license');
      const isValid = await checkLocalLicenseValidity();
      
      const { isTauri } = await import('@/lib/db/db');
      if (isValid || !isTauri()) {
        setLicenseValid(true);
      }
      setDbReady(true);
    }).catch(err => {
      console.error("Erro na inicialização do banco SQLite/Licença:", err);
      setDbReady(true);
      setLicenseValid(true);
    });
  }, [])

  if (!mounted || !dbReady) {
    return <Preloader />;
  }

  if (!licenseValid) {
    return <LicenseActivation onActivated={() => setLicenseValid(true)} />;
  }


  return (
    <ErrorBoundary>
      <Router>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider defaultTheme="light" storageKey="itacondo-theme">
            <TooltipProvider>
              <AuthProvider>
                <ScrollToTop />
                <RouterWrapper />
                <RealtimeSyncManager />
                <OfflineIndicator />
                <Toaster />
                <CookieConsentBanner />
                <Suspense fallback={null}>
                  <PWALifeCycleLazy />
                </Suspense>
              </AuthProvider>
            </TooltipProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </Router>
    </ErrorBoundary>
  );
};


const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = createRoot(rootElement);
root.render(<App />);
